import {
  QMenuButton,
  QMenuItem,
  useToastProvider,
} from '@qualio/ui-components';
import { CurrentUser } from '@qualio/ui-components/lib/types/CurrentUser';
import { useTranslation } from 'react-i18next';
import { backendTaskApi } from '../../../../api/backendTask';
import { DocumentAccount } from '../../../../api/model/document';
import { ExportMethodType } from '../../DocumentWorkspace/types';
import { exportViaMedtechTask } from '../export';
import { ExportFormat } from './types';

const exportFormats = Object.values(ExportFormat);

export const ExportDocumentListButton: React.FC<{
  currentUser: CurrentUser;
  tagsFilter: string[];
  isDisabled: boolean;
  searchQuery: string | null;
  sortingValue: string | null | undefined;
  statusFilters?: string[] | null | undefined;
  templatesFilter?: string[];
  ownerFilter?: number | null;
  accountFilter?: DocumentAccount;
  exportTaskMethod?: ExportMethodType;
}> = ({
  currentUser,
  tagsFilter,
  isDisabled,
  searchQuery,
  sortingValue,
  statusFilters,
  exportTaskMethod,
  templatesFilter,
  ownerFilter,
  accountFilter,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const toastCommonProps = {
    replace: true,
  };
  const onWaitingForExport = (taskId: string) => {
    showToast({
      'data-cy': 'export-document-list-button-export-document-list-info-toast',
      id: `export-document-list-toast-${taskId}`,
      status: 'info',
      description: t('notifications.exportInProgress'),
      duration: 70000,
      ...toastCommonProps,
    });
  };

  const onSuccess = (taskId: string, downloadLink: string) => {
    // Construct the description outside to avoid React error #321
    const description = (
      <span>
        {t('notifications.exportReady')}{' '}
        <a target="_blank" rel="noreferrer" href={downloadLink}>
          {t('labels.clickHere')}
        </a>
      </span>
    );

    showToast({
      'data-cy':
        'export-document-list-button-export-document-list-success-toast',
      id: `export-document-list-toast-${taskId}`,
      status: 'success',
      description,
      duration: 70000,
      ...toastCommonProps,
    });
  };

  const onFailure = (taskId: string, msg = t('notifications.exportFailed')) => {
    showToast({
      'data-cy': 'export-document-list-button-export-document-list-error-toast',
      id: `export-document-list-toast-${taskId}`,
      status: 'error',
      description: msg,
      ...toastCommonProps,
    });
  };

  const handleExport = (format: ExportFormat) => {
    void exportViaMedtechTask({
      currentUser,
      request: {
        format,
        tagsFilter,
        searchQuery,
        orderBy: sortingValue,
        statusFilters,
        templatesFilter,
        ownerId: ownerFilter,
        accountFilter,
      },
      exportTaskMethod:
        exportTaskMethod ?? backendTaskApi.startAllDocumentsExportTask,
      onWaitingForDownload: onWaitingForExport,
      onSuccess,
      onFailure,
    });
  };

  return (
    <QMenuButton
      isDisabled={isDisabled}
      data-cy="exportButton"
      buttonLabel={t('buttons.export')}
      variant={'outline'}
    >
      {exportFormats.map((exportFormat) => (
        <QMenuItem
          key={exportFormat}
          data-cy={`exportFormat${exportFormat.toUpperCase()}Button`}
          onClick={() => handleExport(exportFormat)}
          fontSize={'sm'}
        >
          {exportFormat.toUpperCase()}
        </QMenuItem>
      ))}
    </QMenuButton>
  );
};
